import { ConnectionStatus } from './connection-status.enum';
import { IntegrationVendor } from './integration-vendor.enum';

/**
 * D365 BC vendor-specific identity sub-document.
 * Mirrors backend D365BcConnectionDetails (vendor-keyed bag).
 */
export interface ID365BcConnectionDetails {
    vendorTenantIdentifier: string; // Microsoft Entra Tenant UUID
    vendorEnvironment: string; // e.g. "production"
}

/**
 * Abacus vendor-specific identity sub-document.
 * Mirrors backend AbacusConnectionDetailsEntity (vendor-keyed bag).
 *
 * Sensitive fields (encryptedClientId, encryptedClientSecret, cachedTokenEndpoint*)
 * are stripped server side before responses. FE only sees `serverUrl` for display.
 */
export interface IAbacusConnectionDetails {
    serverUrl: string;
}

/**
 * Topal vendor-specific identity sub-document.
 * Mirrors backend TopalConnectionDetailsEntity (vendor-keyed bag).
 *
 * Sensitive fields (username is a non-secret login id; encryptedPassword) are stripped
 * server side before responses. FE only sees `serverUrl` for display.
 */
export interface ITopalConnectionDetails {
    serverUrl: string;
}

/**
 * Accounto vendor-specific identity sub-document (REV-21119 D1).
 * Mirrors backend AccountoConnectionDetailsEntity (vendor-keyed bag).
 *
 * The encrypted API key is stripped server side before responses. FE only sees the tenant the key
 * belongs to and its kind; the key's expiry is the connection's `tokenExpiresAt`.
 */
export interface IAccountoConnectionDetails {
    tenantId: string;
    /** `ADVISOR` = Treuhand key (lists its client tenants), `CLIENT` = KMU key (its own tenant only). */
    keyKind: 'ADVISOR' | 'CLIENT';
}

/**
 * Proffix vendor-specific identity sub-document.
 * Mirrors backend ProffixConnectionDetailsEntity (vendor-keyed bag).
 *
 * Sensitive fields (encryptedPassword, encryptedRestApiKey) are stripped server side
 * before responses. FE only sees `serverUrl` (normalised origin) and the non-secret
 * `username` for display.
 */
export interface IProffixConnectionDetails {
    serverUrl: string;
    username?: string;
}

/**
 * Per-Organization integration connection (BC, future Abacus, ...).
 * One document per (organization, vendor, vendor-tenant). Multi-instance per vendor possible.
 *
 * Token-fields (cachedAccessToken, encryptedDek) are stripped server-side
 * before responses: FE only sees the metadata.
 */
export interface IIntegrationConnection {
    id: string;
    organizationId: string;
    /**
     * Data residency: every organization is pinned to either CH or DE. The BE filters
     * connection lookups by this implicitly, but the FE surfaces it for support cases
     * ("which region is this tenant in?").
     */
    dataCenter: 'CH' | 'DE';

    vendor: IntegrationVendor;

    // Lifecycle
    status: ConnectionStatus;
    connectedAt: Date;
    lastTestAt: Date | null;
    lastErrorMessage: string | null;
    connectedBy: string;
    displayName: string;
    tokenExpiresAt: Date;

    // Vendor-keyed bag: exactly one populated, matching `vendor`
    d365bc?: ID365BcConnectionDetails;
    abacus?: IAbacusConnectionDetails;
    topal?: ITopalConnectionDetails;
    accounto?: IAccountoConnectionDetails;
    proffix?: IProffixConnectionDetails;

    createdAt?: Date;
    updatedAt?: Date;
}
